<script setup lang="ts">
import { reactive, ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ArrowLeft, AlertTriangle } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Switch } from '@/components/shadcnui/switch';
import { Textarea } from '@/components/shadcnui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcnui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/shadcnui/dialog';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useRelease } from '@/composable/useRelease';
import { useFwtype } from '@/composable/useFwtype';
import { useModel } from '@/composable/useModel';
import { useChipset } from '@/composable/useChipset';
import { usePincode } from '@/composable/usePincode';
import { usePkgtype } from '@/composable/usePkgtype';
import { usePkg } from '@/composable/usePkg';
import { useFw } from '@/composable/useFw';
import { useApp } from '@/composable/useApp';
import { useJSONEditor } from '@/composable/useJSONEditor';

// 타입 임포트
import type { release, fw, Updaterelease, addrelease, fwtypeidver, pkgnameidver } from '../types/types';
// JSON 에디터 임포트
import JsonEditorVue from 'json-editor-vue'

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onItemRelease, onUpdateRelease, onAddRelease, onDeleteRelease } = useRelease();
const { onReadFwtypeList } = useFwtype();
const { onReadModelList } = useModel();
const { onReadChipsetList } = useChipset();
const { onUpdatePincode, onNewPincode } = usePincode();
const { onReadPkgtypeList } = usePkgtype();
const { onReadPkgList } = usePkg();
const { onReadFwList } = useFw();
const { onReadAppList } = useApp();
const { handleRenderMenu, handleContextMenu } = useJSONEditor();

// 라우터 초기화
const router = useRouter();
const route = useRoute();

// 업데이트 모드 상태
const isUpdateMode = !!route.params.id;

// 펌웨어 스토어 키 매핑 ( 0=Full, 1=Patch, 2=Bridge )
const fwStoreKeys = ['fwrs_full', 'fwrs_patch', 'fwrs_bridge'] as const;
// 펌웨어 타입 셀렉트 상태 변수
const fwSelections = reactive<Record<number, number | null>>({});
// 펌웨어 타입에 따른 버전 데이터 리스트
const FirmwareType = reactive<Record<number, fwtypeidver[]>>({});

// 앱 패키지 스토어 키 매핑 ( 0=Launcher, 1=Service, 2=Upgrade, 3=Rcuapp, 4=Helpcenter, 5=Tunerservice, 6=Networkstorage, 7=Market, 8=Mol )
const appStoreKeys = [
  'apprs_launcher',
  'apprs_service',
  'apprs_upgrade',
  'apprs_rcuapp',
  'apprs_helpcenter',
  'apprs_tunerservice',
  'apprs_networkstorage',
  'apprs_market',
  'apprs_mol',
] as const;
// 앱 패키지 셀렉트 상태 변수
const appSelections = reactive<Record<number, number>>({});
// 앱 패키지에 따른 버전 데이터
const AppsVersion = reactive<Record<number, pkgnameidver[]>>({});

// 전체 폼 데이터 상태
const formData = ref<release>({
  id: 0,
  chipset_id: 0,
  model_id: 0,
  title: "",
  descr: "",
  relnote: "",
  fwrs_id: 0,
  fwrs_full: 0,
  fwrs_patch: 0,
  fwrs_bridge: 0,
  apprs_id: 0,
  apprs_launcher: 0,
  apprs_service: 0,
  apprs_upgrade: 0,
  apprs_helpcenter: 0,
  apprs_tunerservice: 0,
  apprs_networkstorage: 0,
  apprs_mol: 0,
  apprs_market: 0,
  apprs_rcuapp: 0,
  isuse: 0,
  isbeta: 0,
  pincode_id: 0,
  pincode_code: '0',
  pincode_used: 0,
  pincode_count: 0,
  pincode_total: 0,
  priority_id: 0,
  dlcount: 0,
  dllimit: 9999999,
});

const jsonData = ref({
  title: "",
  date: "",
  notes: [{
    title: "",
    info: [""]
  }]
});

const isDeleteDialogOpen = ref(false);
const isOnMounted = ref(true);

// 칩셋이 변경되면 모델 새로고침
watch(() => formData.value.chipset_id, async () => {
  if (isUpdateMode) return;
  formData.value.model_id = 0; // 모델 초기화
  Object.keys(AppsVersion).forEach(key => {
    delete AppsVersion[Number(key)];
  });
  Object.keys(FirmwareType).forEach(key => {
    delete FirmwareType[Number(key)];
  });
  await onReadModelList({ chipset_id: formData.value.chipset_id, page: 0, size: 0 });
});

// 칩셋 또는 모델 변경 시 펌웨어 및 앱 버전 업데이트
watch([() => formData.value.chipset_id, () => formData.value.model_id], async () => {
  // 칩셋과 모델이 모두 선택된 경우에만 펌웨어 및 앱 버전 리스트 업데이트
  if (formData.value.model_id === 0 || formData.value.chipset_id === 0) {
    console.log("칩셋 또는 모델이 선택되지 않음, 펌웨어 및 앱 리스트 업데이트 건너뜀");
    return;
  }
  // 업데이트 모드가 아닐 때만 펌웨어 및 앱 리스트 API 호출
  if (!isUpdateMode) {
    await onReadFwList({ model_id: 0, chipset_id: 0, fwtype_id: 0, page: 0, size: 0 });
    await onReadAppList({ chipset_id: formData.value.chipset_id, package_id: 0, page: 0, size: 0 });
  }
  // 업데이트 모드가 아닐 때만 초기값 설정 
  if (!isUpdateMode) {
    FwTypeSet();
    AddFirmwareSet();
    AppsVersionSet();
    ActiveAppsSet();
  }
});

// 펌웨어 타입 선택 시 다른 펌웨어 타입 초기화
// Full(0),Patch(1) 선택시 Bridge(2) 초기화
// Bridge(2) 선택시 Full(0), Patch(1) 초기화
watch(() => fwSelections[0], (newVal) => {
  if (isOnMounted.value) return; // onMounted 시에는 실행하지 않음
  if (newVal === null || newVal === 0) return; // 자기가 초기화될 때는 멈춤
  const temp = FirmwareType[0].find(f => f.id === newVal);
  // Full 선택시 Patch 에 같은 데이터 있으면 선택
  const id = FirmwareType[1].find(f => f.fwvername === temp?.fwvername && f.fwvervalue === temp?.fwvervalue)?.id;
  if (id) {
    fwSelections[1] = id;
  }
  else {
    fwSelections[1] = null;
  }
  fwSelections[2] = null;
});
watch(() => fwSelections[1], (newVal) => {
  if (isOnMounted.value) return; // onMounted 시에는 실행하지 않음
  if (newVal === null || newVal === 0) return;
  const temp = FirmwareType[1].find(f => f.id === newVal);
  // Patch 선택시 Full 에 같은 데이터 있으면 선택
  const id = FirmwareType[0].find(f => f.fwvername === temp?.fwvername && f.fwvervalue === temp?.fwvervalue)?.id;
  if (id) {
    fwSelections[0] = id;
  }
  else {
    fwSelections[0] = null;
  }
  fwSelections[2] = null;
});
watch(() => fwSelections[2], (newVal) => {
  if (isOnMounted.value) return; // onMounted 시에는 실행하지 않음
  if (newVal === null || newVal === 0) return;
  fwSelections[0] = null;
  fwSelections[1] = null;
});

// 펌웨어 타입별 버전 리스트 세팅 함수
const FwTypeSet = () => {
  for (let i = 0; i < 3; i++) {
    // 펌웨어 타입 필터링 : fwtype_id, chipset_id, model_id로 구분
    FirmwareType[i] = RSStore.fwList.filter(f => f.fwtype_id === RSStore.fwtypeList[i].id
      && f.chipset_id === formData.value.chipset_id
      && f.model_id === formData.value.model_id
      && f.isshow === 1
    ).map((fw: fw) => ({ id: fw.id, fwvername: fw.fwvername, fwvervalue: fw.fwvervalue, tgvername: fw.tgvername }))
  }
}

// 펌웨어 선택 규칙 : PATCH/FULL 동시 존재 시: 높은 버전 우선
const ActiveFirmwareSet = () => {
  if (RSStore.releaseItem?.fwrs_full !== 0) {
    fwSelections[0] = RSStore.releaseItem?.fwrs_full ?? null;
  }
  if (RSStore.releaseItem?.fwrs_patch !== 0) {
    fwSelections[1] = RSStore.releaseItem?.fwrs_patch ?? null;
  }
  if (RSStore.releaseItem?.fwrs_bridge !== 0) {
    fwSelections[2] = RSStore.releaseItem?.fwrs_bridge ?? null;
  }
}
// 새 릴리즈 페이지 진입 시 펌웨어 초기값 세팅 함수
const AddFirmwareSet = () => {
  if (FirmwareType[0].length > 0) {
    fwSelections[0] = FirmwareType[0].reduce((prev, current) => (prev.fwvervalue > current.fwvervalue) ? prev : current)?.id;
  }
}

// 앱 패키지 버전 리스트 세팅 함수
const AppsVersionSet = () => {
  for (let i = 0; i < 9; i++) {
    // 앱 패키지 타입 필터링 : pkgtype_id, chipset_id로 구분
    AppsVersion[i] = RSStore.appList.filter(app => app.package_id === (RSStore.pkgList.find(pkg => pkg.chipset_id === formData.value.chipset_id
      && pkg.pkgtype_id === RSStore.pkgtypeList[i].id)?.id)
      && app.isshow === 1)
  }
}
//앱 패키지 목록에서 vercode값이 가장 높은 버전으로 활성화
const ActiveAppsSet = () => {
  appStoreKeys.forEach((storeKey, idx) => {
    const id = idx;
    // 스토어에 앱 버전이 존재하는 경우에만 설정하도록 조건 추가
    if (AppsVersion[id] && AppsVersion[id].length !== 0) {
      // 각 앱 유형에 대해 릴리스 아이템에 설정된 버전이 없으면 해당 유형의 앱 목록에서 vercode값이 가장 높은 버전으로 설정
      appSelections[id] = AppsVersion[id].reduce((prev, current) => (prev.vercode > current.vercode) ? prev : current)?.id;
    } else {
      appSelections[id] = RSStore.releaseItem?.[storeKey] as number;
    }
  });
};

// 새 릴리즈 페이지 진입 시 앱 버전 초기값 세팅 함수
const AddAppsSet = () => {
  appStoreKeys.forEach((storeKey, idx) => {
    const id = idx;
    appSelections[id] = RSStore.releaseItem?.[storeKey] as number;
  });
};

// use, beta 토글 핸들러
const toggleUse = () => {
  if (formData.value.isuse) {
    formData.value.isuse = 0
  }
  else {
    formData.value.isuse = 1
  }
}
const toggleBeta = () => {
  if (formData.value.isbeta) {
    formData.value.isbeta = 0
  }
  else {
    formData.value.isbeta = 1
  }
}

// 폼 제출 핸들러
const handleSubmit = async (e: Event) => {
  e.preventDefault();
  UserStore.relfilter.chipset_id = 0;
  UserStore.relfilter.model_id = 0;
  router.push("/releases");
  UserStore.relfilter.page = 1;
  // 펌웨어 및 앱 버전 선택이 없는 경우 0으로 설정
  fwStoreKeys.forEach((storeKey, idx) => {
    const id = idx;
    formData.value[storeKey] = fwSelections[id] ? fwSelections[id]! : 0;
  });
  appStoreKeys.forEach((storeKey, idx) => {
    const id = idx;
    formData.value[storeKey] = appSelections[id] ? appSelections[id] : 0;
  });

  // 핀코드 사용 여부에 따른 데이터 정리
  if (formData.value.pincode_used === 0) {
    if (!formData.value.pincode_used && formData.value.pincode_id) {
    }
    formData.value.pincode_id = 0;
    formData.value.pincode_code = '0';
    formData.value.pincode_count = 0;
    formData.value.pincode_total = 0;
  }
  // 업데이트 모드와 추가 모드에 따른 API 호출
  if (isUpdateMode) {
    console.log("UpdateRelease Data: ", formData.value);
    const updateRelease: Updaterelease = {
      id: formData.value.id,
      chipset_id: formData.value.chipset_id,
      model_id: formData.value.model_id,
      title: formData.value.title,
      descr: formData.value.descr,
      relnote: btoa(JSON.stringify(jsonData.value)),
      isuse: formData.value.isuse,
      priority_id: formData.value.priority_id,
      dllimit: formData.value.dllimit,
      isbeta: formData.value.isbeta,
      fwrs_full: formData.value.fwrs_full,
      fwrs_patch: formData.value.fwrs_patch,
      fwrs_bridge: formData.value.fwrs_bridge,
      apprs_launcher: formData.value.apprs_launcher,
      apprs_service: formData.value.apprs_service,
      apprs_upgrade: formData.value.apprs_upgrade,
      apprs_helpcenter: formData.value.apprs_helpcenter,
      apprs_tunerservice: formData.value.apprs_tunerservice,
      apprs_networkstorage: formData.value.apprs_networkstorage,
      apprs_mol: formData.value.apprs_mol,
      apprs_market: formData.value.apprs_market,
      apprs_rcuapp: formData.value.apprs_rcuapp,
      pin_id: formData.value.pincode_id,
      pin_used: formData.value.pincode_used ?? 0,
      pin_total: formData.value.pincode_total ?? 0,
      pin_code: formData.value.pincode_code ?? '0',
    };

    await onUpdateRelease(updateRelease);
  } else {
    const addRelease: addrelease = {
      chipset_id: formData.value.chipset_id,
      model_id: formData.value.model_id,
      title: formData.value.title,
      descr: formData.value.descr,
      relnote: btoa(JSON.stringify(jsonData.value)),
      isuse: formData.value.isuse,
      priority_id: formData.value.priority_id,
      dllimit: formData.value.dllimit,
      isbeta: formData.value.isbeta,
      fwrs_full: formData.value.fwrs_full,
      fwrs_patch: formData.value.fwrs_patch,
      fwrs_bridge: formData.value.fwrs_bridge,
      apprs_launcher: formData.value.apprs_launcher,
      apprs_service: formData.value.apprs_service,
      apprs_upgrade: formData.value.apprs_upgrade,
      apprs_helpcenter: formData.value.apprs_helpcenter,
      apprs_tunerservice: formData.value.apprs_tunerservice,
      apprs_networkstorage: formData.value.apprs_networkstorage,
      apprs_mol: formData.value.apprs_mol,
      apprs_market: formData.value.apprs_market,
      apprs_rcuapp: formData.value.apprs_rcuapp,
      pin_id: formData.value.pincode_id,
      pin_used: formData.value.pincode_used ?? 0,
      pin_total: formData.value.pincode_total ?? 0,
      pin_code: formData.value.pincode_code ?? '0',
    };
    await onAddRelease(addRelease);
    console.log("AddRelease Data: ", addRelease);
    UserStore.relcurrentPage = 1;
    UserStore.relfilter.chipset_id = 0;
    UserStore.relfilter.model_id = 0;
  }
};

const handleDelete = async (id: number) => {
  await onDeleteRelease(id);
  UserStore.relcurrentPage = 1;
  UserStore.relfilter.chipset_id = 0;
  UserStore.relfilter.model_id = 0;
  router.push("/releases");
};

// pincode 활성화/비활성화 및 업데이트 핸들러
// pincode_code == 0 경우 pincode 생성
const pinActive = async () => {
  if (formData.value.pincode_used) {
    formData.value.pincode_used = 0;
  } else {
    formData.value.pincode_used = 1;
    if (formData.value.pincode_code === "0" || formData.value.pincode_code === null) {
      await onNewPincode();
      formData.value.pincode_id = RSStore.pincode?.id || 0;
      formData.value.pincode_code = RSStore.pincode?.code || '0';
      formData.value.pincode_count = RSStore.pincode?.count || 0;
      formData.value.pincode_total = RSStore.pincode?.total || 0;
    }
  }
}
// 핀코드 업데이트 핸들러 
const handleUpdatePin = async () => {
  await onUpdatePincode(formData.value.pincode_id);
  formData.value.pincode_code = RSStore.pincode?.code || '0';
}
// 취소 핸들러
const handleCancel = () => {
  router.push("/releases");
};

// JSON 에디터 변경 핸들러
const onJsonChange = (value: any) => {
  console.log('변경된 데이터:', value);
  jsonData.value = value;
};

onMounted(async () => {
  await onReadChipsetList({ page: 0, size: 0 });
  await onReadModelList({ chipset_id: 0, page: 0, size: 0 });
  await onReadFwtypeList({ page: 0, size: 0 });
  await onReadPkgtypeList({ page: 0, size: 0 });
  await onReadFwList({ model_id: 0, chipset_id: 0, fwtype_id: 0, page: 0, size: 0 });
  await onReadPkgList({ chipset_id: 0, pkgtype_id: 0, page: 0, size: 0 });
  await onReadAppList({ chipset_id: 0, package_id: 0, page: 0, size: 0 });
  if (isUpdateMode) {
    await onItemRelease(Number(route.params.id));
    formData.value = { ...RSStore.releaseItem } as release;
    if (formData.value.relnote) {
      jsonData.value = JSON.parse(atob(formData.value.relnote));
    }
    else {
      jsonData.value = {
        title: "",
        date: "",
        notes: [{
          title: "",
          info: [""]
        }]
      };
    }
    if (formData.value.pincode_code === "0" || formData.value.pincode_code === null) {
      formData.value.pincode_used = 0;
      formData.value.pincode_id = 0;
      formData.value.pincode_count = 0;
      formData.value.pincode_total = 0;
    }
  }
  else {
    RSStore.releaseItem = {
      id: 0,
      chipset_id: 0,
      model_id: 0,
      title: '',
      descr: '',
      relnote: '',
      // 펌웨어 관련 초기화
      fwrs_id: 0,
      fwrs_full: 0,
      fwrs_patch: 0,
      fwrs_bridge: 0,
      // 앱 관련 초기화
      apprs_id: 0,
      apprs_launcher: 0,
      apprs_service: 0,
      apprs_upgrade: 0,
      apprs_helpcenter: 0,
      apprs_tunerservice: 0,
      apprs_networkstorage: 0,
      apprs_mol: 0,
      apprs_market: 0,
      apprs_rcuapp: 0,
      isuse: 0,
      isbeta: 0,
      // 핀코드 관련 초기화
      pincode_id: 0,
      pincode_code: '0',
      pincode_used: 0,
      pincode_count: 0,
      pincode_total: 0,
      priority_id: 0,
      dlcount: 0,
      dllimit: 0
    };
  }
  await AddAppsSet();
  await AppsVersionSet();
  await FwTypeSet();
  await ActiveFirmwareSet();
  isOnMounted.value = false;
})
</script>

<template>
  <div class="p-8 max-w-8/10 mx-auto">
    <div class="mb-6 text-foreground">
      <Button variant="ghost" size="sm" class="mb-4" @click="handleCancel">
        <ArrowLeft class="mr-2 h-4 w-4" />
        목록으로
      </Button>
      <h1 class="text-3xl font-bold">{{ isUpdateMode ? '릴리즈 수정' : '릴리즈 추가' }}</h1>
    </div>
    <Card>
      <CardHeader>
        <CardTitle>릴리즈 정보</CardTitle>
      </CardHeader>
      <CardContent>
        <div v-if="formData" class="grid grid-cols-10 gap-8 gap-8">
          <div class="space-y-4 col-span-6">
            <!-- chipset Select 메뉴-->
            <div class="space-y-2">
              <Label for="chipset">Chipset</Label>
              <Select v-model="formData.chipset_id" :disabled="isUpdateMode">
                <SelectTrigger id="chipset">
                  <SelectValue placeholder="선택하세요" />
                </SelectTrigger>
                <SelectContent class="w-[var(--reka-select-trigger-width)]">
                  <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id"
                    class="w-full">
                    {{ chipset.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <!-- Model Select 메뉴 -->
            <div class="space-y-2">
              <Label for="model">Model</Label>
              <Select v-model="formData.model_id" :disabled="isUpdateMode || formData.chipset_id === 0">
                <SelectTrigger id="model">
                  <SelectValue placeholder="선택하세요" />
                </SelectTrigger>
                <SelectContent class="w-[var(--reka-select-trigger-width)]">
                  <SelectItem v-for="model in RSStore.modelList" :key="model.id" :value="model.id" class="w-full">
                    {{ model.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <!-- Title, Description, Release Note, Download Count, Download Limit 입력창 -->
            <div class="space-y-2">
              <Label for="title">Title</Label>
              <Input id="title" v-model="formData.title" placeholder="제목을 입력하세요" maxlength="128"
                :disabled="isUpdateMode && !UserStore.releaseEdit" />
            </div>
            <div class="space-y-2">
              <Label for="description">Description</Label>
              <Textarea id="description" v-model="formData.descr" placeholder="설명을 입력하세요" class="min-h-[100px]"
                :disabled="isUpdateMode && !UserStore.releaseEdit" />
            </div>
            <div class="space-y-2">
              <Label for="releaseNote">Release Note</Label>
              <JsonEditorVue :read-only="isUpdateMode && !UserStore.releasenoteEdit" class="h-140" v-model="jsonData"
                :mainMenuBar="true" :navigation-bar="false" :onRenderMenu="handleRenderMenu"
                :onRenderContextMenu="handleContextMenu" @update:modelValue="onJsonChange" />
            </div>
            <div class="space-y-2">
              <Label for="downloadCount">Download Count</Label>
              <Input id="downloadCount" type="number" v-model="formData.dlcount" :disabled="true" />
            </div>
            <div class="space-y-2">
              <Label for="downloadLimit">Download Limit</Label>
              <Input id="downloadLimit" type="number" v-model="formData.dllimit"
                :disabled="isUpdateMode && !UserStore.releaseEdit" />
            </div>
            <!-- Use 및 Beta 토글 스위치 -->
            <div class="flex gap-6 pt-2">
              <div class="flex items-center space-x-2">
                <Switch id="use" :model-value="Boolean(formData.isuse)" @click="toggleUse"
                  :disabled="isUpdateMode && !UserStore.releaseEdit" />
                <Label for="use" class="cursor-pointer text-foreground">Use</Label>
              </div>
              <div class="flex items-center space-x-2">
                <Switch id="beta" :model-value="Boolean(formData.isbeta)" @click="toggleBeta"
                  :disabled="isUpdateMode && !UserStore.releaseEdit" />
                <Label for="beta" class="cursor-pointer text-foreground">Beta</Label>
              </div>
            </div>
          </div>
          <div class="space-y-6 col-span-4">
            <Card class="bg-muted/30">
              <CardHeader class="pb-3">
                <CardTitle class="text-lg">펌웨어 (Firmware)</CardTitle>
              </CardHeader>
              <!-- 펌웨어 타입 선택 메뉴 -->
              <CardContent class="space-y-4">
                <div v-if="RSStore.fwtypeList.length >= 3" class="space-y-3">
                  <div class="grid grid-cols-10 gap-4">
                    <template v-for="(fwtype, idx) in RSStore.fwtypeList.slice(0, 3)" :key="fwtype.id">
                      <Label class="px-3 col-span-3">
                        {{ fwtype.name }}
                      </Label>
                      <Select v-model="fwSelections[idx]"
                        :disabled="FirmwareType[idx] === undefined || FirmwareType[idx].length === 0 || (isUpdateMode && !UserStore.releaseEdit)">
                        <SelectTrigger class="col-span-7">
                          <SelectValue :placeholder="`사용안함`" />
                        </SelectTrigger>
                        <SelectContent class="w-[var(--reka-select-trigger-width)]">
                          <SelectItem class="w-full" v-for="type in FirmwareType[idx]" :key="type.id" :value="type.id">
                            {{ type.fwvername + ' | ' + type.tgvername }}
                          </SelectItem>
                          <SelectItem class="w-full" :key="0" :value="null">
                            {{ "사용안함" }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </template>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card class="bg-muted/30">
              <CardHeader class="pb-3">
                <CardTitle class="text-lg">앱 (Application)</CardTitle>
              </CardHeader>
              <CardContent>
                <!-- 패키지 앱 선택 메뉴 -->
                <div v-if="RSStore.pkgtypeList.length >= 8">
                  <div class="grid grid-cols-1 gap-3">
                    <div v-for="(pkgtype, idx) in RSStore.pkgtypeList.slice(0, 9)" :key="pkgtype.id"
                      class="grid grid-cols-10 gap-4">
                      <Label class="px-3 col-span-3">
                        {{ pkgtype.name }}
                      </Label>
                      <Select v-model="appSelections[idx]"
                        :disabled="AppsVersion[idx] === undefined || AppsVersion[idx].length === 0 || (isUpdateMode && !UserStore.releaseEdit)">
                        <SelectTrigger class="col-span-7">
                          <SelectValue :placeholder="`사용안함`" />
                        </SelectTrigger>
                        <SelectContent class="w-[var(--reka-select-trigger-width)]">
                          <SelectItem class="w-full" v-for="version in AppsVersion[idx]" :key="version.id"
                            :value="version.id">
                            {{ version.vercode + ' | ' + version.vername }}
                          </SelectItem>
                          <SelectItem class="w-full" :key="0" :value="null">
                            {{ "사용안함" }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <!-- 핀코드 사용 여부 및 핀 업데이트 버튼 -->
            <Card class="bg-muted/30">
              <CardHeader class="pb-3 grid grid-cols-2 gap-4">
                <CardTitle class="text-lg">핀 (Pin)</CardTitle>
                <CardTitle class="text-lg">업데이트 핀 (Pin)</CardTitle>
              </CardHeader>
              <CardContent class="space-y-4">
                <div class="pb-3 grid grid-cols-2 gap-4">
                  <div class="flex items-center space-x-2">
                    <Switch id="usePin" @click="pinActive()" :model-value="Boolean(formData.pincode_used)"
                      :disabled="isUpdateMode && !UserStore.releaseEdit" />
                    <Label for="usePin" class="cursor-pointer text-foreground">Use Pin</Label>
                  </div>
                  <div class="flex items-center space-x-2">
                    <Button id="usePin" type="button" variant="outline" @click="handleUpdatePin"
                      :disabled="!formData.pincode_used || (isUpdateMode && !UserStore.releaseEdit)">
                      Update Pin
                    </Button>
                  </div>
                </div>
                <div class="space-y-2">
                  <Label class="text-xs">Pin Code</Label>
                  <div class="flex items-center space-x-2">
                    <Input :model-value="formData.pincode_code ?? '0'" :disabled="true" />
                  </div>
                </div>
                <div class="space-y-2">
                  <div class="pb-3 grid grid-cols-2 gap-4">
                    <Label class="text-xs">Count</Label>
                    <Label class="text-xs space-x-2">Total</Label>
                  </div>
                  <div class="flex items-center space-x-2">
                    <Input type="number" :model-value="formData.pincode_count ?? '0'" :disabled="true" />
                    <Input type="number" v-model="formData.pincode_total"
                      :disabled="!formData.pincode_used || (isUpdateMode && !UserStore.releaseEdit)" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <div class="flex items-center gap-2 mt-8 border-t pt-6">
          <Button type="submit" size="lg" @click="handleSubmit"
            :disabled="(!UserStore.releaseEdit && !UserStore.releasenoteEdit) && isUpdateMode">
            {{ isUpdateMode ? 'Modify' : 'Add' }}
          </Button>
          <Button v-if="isUpdateMode && UserStore.releaseDelete" size="lg" variant="destructive"
            @click="isDeleteDialogOpen = true" :disabled="!UserStore.releaseDelete">
            Delete
          </Button>
          <Button type="button" variant="outline" size="lg" @click="handleCancel">Cancel</Button>
        </div>
      </CardContent>
    </Card>

    <!-- 릴리즈 삭제 확인창 -->
    <Dialog v-model:open="isDeleteDialogOpen">
      <form>
        <DialogContent class="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              <div class="flex items-center gap-2">
                <AlertTriangle class="w-6 h-6 text-red-500" />
                릴리즈 삭제
              </div>
            </DialogTitle>
            <hr class="my-2 bg-" />
            <DialogDescription style="white-space: pre-line;">
              <div class="my-4 rounded-md border bg-muted/40 p-4 space-y-3 text-sm">
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Title</span>
                  <span class="text-muted-foreground font-medium"> {{ formData.title }}</span>
                </div>
              </div>
                <p class="text-muted-foreground text-red-500">
                릴리즈를 삭제하시겠습니까?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" @click="isDeleteDialogOpen = false">
              Cancel
            </Button>
            <Button @click="handleDelete(formData.id)">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </form>
    </Dialog>
  </div>

</template>

<style scoped>
/* 에디터 컨테이너 클래스 내부의 변수를 수정 */
:deep(.jse-main) {
  /* 메뉴바 전체 배경색 */
  --jse-theme-color: #000000;
  --jse-theme-color-highlight: #333333;
}
</style>